import { useCallback, useRef, useEffect } from 'react';
import { useAtom } from 'jotai';
import { trackGestureEvents } from '../../lib/analyticsEnhanced';
import { taskV2TimelineScaleAtom } from '../../atoms/v3/taskV2Atoms';

interface PinchZoomOptions {
  minScale?: number;
  maxScale?: number;
  sensitivity?: number;
  enabled?: boolean;
}

/**
 * Pinch 缩放手势 Hook
 * 支持移动端双指缩放和 Mac 触摸板缩放
 */
export const usePinchZoom = (options: PinchZoomOptions = {}) => {
  const {
    minScale = 0.5,
    maxScale = 3.0,
    sensitivity = 1.0,
    enabled = true
  } = options;

  const [scale, setScale] = useAtom(taskV2TimelineScaleAtom);
  
  // 手势状态
  const gestureStateRef = useRef({
    isGesturing: false,
    initialDistance: 0,
    initialScale: 1.0,
    lastScale: 1.0
  });

  // 计算两点间距离
  const getDistance = useCallback((touch1: Touch, touch2: Touch) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }, []);

  // 处理触摸开始
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled || e.touches.length !== 2) {
      gestureStateRef.current.isGesturing = false;
      return;
    }

    e.preventDefault();

    const distance = getDistance(e.touches[0], e.touches[1]);
    gestureStateRef.current = {
      isGesturing: true,
      initialDistance: distance,
      initialScale: scale,
      lastScale: scale
    };

    // 记录缩放手势开始
    trackGestureEvents.gesturePerformed('pinch_start', {
      initial_scale: scale,
      touch_count: e.touches.length
    });
  }, [enabled, scale, getDistance]);

  // 处理触摸移动
  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!enabled || !gestureStateRef.current.isGesturing || e.touches.length !== 2) {
      return;
    }

    e.preventDefault();

    const currentDistance = getDistance(e.touches[0], e.touches[1]);
    const scaleRatio = currentDistance / gestureStateRef.current.initialDistance;
    
    // 应用敏感度调整
    const adjustedRatio = 1 + (scaleRatio - 1) * sensitivity;
    
    const newScale = Math.max(
      minScale,
      Math.min(maxScale, gestureStateRef.current.initialScale * adjustedRatio)
    );

    // 只在变化足够大时更新（减少频繁更新）
    if (Math.abs(newScale - gestureStateRef.current.lastScale) > 0.05) {
      setScale(newScale);
      gestureStateRef.current.lastScale = newScale;
    }
  }, [enabled, getDistance, minScale, maxScale, sensitivity, setScale]);

  // 处理触摸结束
  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (!enabled) return;

    if (e.touches.length < 2) {
      const wasGesturing = gestureStateRef.current.isGesturing;
      gestureStateRef.current.isGesturing = false;

      // 记录缩放手势结束
      if (wasGesturing) {
        trackGestureEvents.gesturePerformed('pinch_end', {
          final_scale: scale,
          initial_scale: gestureStateRef.current.initialScale,
          scale_change: scale - gestureStateRef.current.initialScale
        });
      }
    }
  }, [enabled, scale]);

  // 处理 Mac 触摸板手势（wheel 事件）
  const handleWheel = useCallback((e: WheelEvent) => {
    if (!enabled) return;

    // 检测是否是 pinch 手势（ctrlKey 通常表示缩放手势）
    if (e.ctrlKey) {
      e.preventDefault();
      
      // wheelDelta 或 deltaY 用于检测缩放方向
      const delta = e.deltaY || -(e as any).wheelDelta;
      const scaleFactor = 1 - (delta * 0.01 * sensitivity);
      
      const newScale = Math.max(
        minScale,
        Math.min(maxScale, scale * scaleFactor)
      );
      
      setScale(newScale);
    }
  }, [enabled, scale, minScale, maxScale, sensitivity, setScale]);

  // 绑定事件监听器的函数
  const bindEvents = useCallback((element: HTMLElement) => {
    if (!enabled) return () => {};

    // 移动端触摸事件
    element.addEventListener('touchstart', handleTouchStart, { passive: false });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: false });
    
    // Mac 触摸板事件
    element.addEventListener('wheel', handleWheel, { passive: false });

    // 返回清理函数
    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('wheel', handleWheel);
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd, handleWheel]);

  return {
    scale,
    isGesturing: gestureStateRef.current.isGesturing,
    bindEvents
  };
};
